import { Inject, Injectable, NgZone, OnDestroy, Optional } from "@angular/core";
import { Store } from "@ngrx/store";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from "@obg/signalr-temp-client";
import { Observable, Observer } from "rxjs";
import { tap } from "rxjs/operators";

import { RealTimeConnectOptions, RealTimeError, RealTimeMessageEvent, RealTimeSelector } from "./real-time.model";
import { SignalrConfigService } from "./signalr.config";
import { SIGNALR_CONST } from "./signalr.const";

@Injectable({
	providedIn: "root",
})
export class SignalrService implements OnDestroy {
	private logger: Log;
	private session: HubConnection | undefined;

	private readonly isAcnkowledgementEnabled = this.config.isAcknowledgementEnabled();

	private customerId: string | undefined;
	private customerId$$ = this.store
		.select(RealTimeSelector.getCustomerId)
		.pipe(
			tap((customerId) => {
				this.customerId = customerId;
			})
		)
		.subscribe();

	constructor(
		logging: LoggingFactory,
		private ngZone: NgZone,
		private config: SignalrConfigService,
		private store: Store<any>,
		@Optional() @Inject("REALTIME_CLIENT_ID") private sourceId = "SignalrClient"
	) {
		this.logger = logging.get(this.sourceId);
	}

	ngOnDestroy() {
		this.customerId$$.unsubscribe();
	}

	connect({ url, enableReconnect, onDisconnect, onReconnect }: RealTimeConnectOptions): Promise<void> {
		if (this.isConnected()) {
			return Promise.reject(RealTimeError.AlreadyExists);
		}
		return this.ngZone.runOutsideAngular(() => {
			let connection = new HubConnectionBuilder().withUrl(url, {
				skipNegotiation: true,
				transport: HttpTransportType.WebSockets,
			});

			if (enableReconnect) {
				connection = connection.withAutomaticReconnect();
			}

			const session = connection.build();
			session.onreconnected(() => {
				this.logger.debug("connect", " reconnected!");
				if (onReconnect) {
					onReconnect();
				}
			});
			session.onclose((errorResponse) => this.handleClosedConnection(errorResponse, onDisconnect));

			return session.start().then(() => {
				this.session = session;
			});
		});
	}

	disconnect(): Promise<void> {
		if (!this.isConnected() || !this.session) {
			return Promise.resolve();
		}

		return this.session.stop();
	}

	isConnected(): boolean {
		return this.session?.state === HubConnectionState.Connected;
	}

	sendMessage(channel: string, message: any): Promise<void> {
		if (!this.session) {
			return Promise.reject(RealTimeError.NoSession);
		}

		const messageContent = JSON.stringify(message);
		return this.session.send(channel, messageContent).then(() =>
			this.logger.debug("sendMessage", "Message sent", {
				channel,
				message: messageContent,
			})
		);
	}

	mapAcknowledgePayload(channel: string, group: boolean, messageIdentifier?: any): string[] {
		const payload = [
			messageIdentifier.sendId,
			this.customerId || undefined,
			HttpTransportType.WebSockets.toString(),
			`${(navigator as any)?.connection?.type}_${(navigator as any)?.connection?.effectiveType}`,
			channel,
			"",
		];
		if (!group) {
			payload.unshift(messageIdentifier.activityId);
		}

		return payload;
	}

	subscribeToChannel<T>(channel: string, group?: string): Observable<RealTimeMessageEvent<T>> {
		return new Observable((observer: Observer<RealTimeMessageEvent<T>>) => {
			const onNext = (value: any, messageIdentifier?: any) => {
				if (this.isAcnkowledgementEnabled && messageIdentifier) {
					const acknowledgePayload = this.mapAcknowledgePayload(channel, !!group, messageIdentifier);

					this.session
						?.invoke(SIGNALR_CONST.invokeAcknowledgeReceiptAsync, ...acknowledgePayload)
						.then()
						.catch((error) =>
							this.logger.warn(
								"onNext::acknowledge",
								`${SIGNALR_CONST.invokeAcknowledgeReceiptAsync} Invoke failed for ${
									group || ""
								} ${JSON.stringify(messageIdentifier)}`,
								error
							)
						);
				}
				this.ngZone.run(() => observer.next({ message: value, channel }));
			};
			const onComplete = () => this.ngZone.run(() => observer.complete());

			if (!this.isConnected() || !this.session) {
				observer.error(new Error(RealTimeError.NoSession));
				onComplete();
				return undefined;
			}

			if (group) {
				this.session
					.invoke(SIGNALR_CONST.invokeSubscribeToGroupAsync, group)
					.then()
					.catch((error) =>
						this.logger.warn(
							"subscribeToChannel",
							`${SIGNALR_CONST.invokeSubscribeToGroupAsync} Invoke failed for ${channel} ${group}`,
							error
						)
					);
			}
			this.session.on(channel, onNext);
			this.session.onclose(onComplete);

			return () => {
				this.logger.debug("subscribeToChannel", "disposing stream", channel);
				if (group) {
					this.session
						?.invoke(SIGNALR_CONST.invokeUnsubscribeFromGroupAsync, group)
						.then()
						.catch((error) =>
							this.logger.warn(
								"subscribeToChannel",
								`${SIGNALR_CONST.invokeUnsubscribeFromGroupAsync} Invoke failed for ${channel} ${group}`,
								error
							)
						);
				}
				this.session?.off(channel, onNext);
			};
		});
	}

	private handleClosedConnection(
		errorResponse: Error | undefined,
		onDisconnect: RealTimeConnectOptions["onDisconnect"]
	) {
		this.session = undefined;
		if (errorResponse) {
			this.logger.error("connect", errorResponse);
		} else {
			this.logger.debug("connect", "connection closed!");
		}

		if (onDisconnect) {
			onDisconnect();
		}
	}
}
